import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { SidebarTrigger } from "@/components/ui/sidebar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { ApiException } from "@/lib/api";
import { useAuthStore } from "@/stores/auth.store";
import { useUsersStore } from "@/stores/users.store";
import type { ReadUserDto } from "@package/types";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

const ROLES = ["USER", "MODERATOR", "ADMIN", "REVIEWER"] as const;

type Role = (typeof ROLES)[number];

const emptyForm = {
  name: "",
  email: "",
  password: "",
  role: "USER" as Role
};

export function AdminUsersPage() {
  const { t } = useTranslation();
  const currentUser = useAuthStore((s) => s.user);
  const users = useUsersStore((s) => s.users);
  const isLoading = useUsersStore((s) => s.isLoading);
  const loadUsers = useUsersStore((s) => s.loadUsers);
  const createUser = useUsersStore((s) => s.createUser);
  const updateUser = useUsersStore((s) => s.updateUser);
  const deleteUser = useUsersStore((s) => s.deleteUser);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [isSaving, setIsSaving] = useState(false);

  const [editUser, setEditUser] = useState<ReadUserDto | null>(null);
  const [userToDelete, setUserToDelete] = useState<ReadUserDto | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const roleLabel: Record<Role, string> = {
    USER: t("admin.roleUser"),
    MODERATOR: t("admin.roleModerator"),
    ADMIN: t("admin.roleAdmin"),
    REVIEWER: t("admin.roleReviewer")
  };

  const showError = (error: unknown) =>
    toast.error(t("common.error"), {
      description: error instanceof ApiException ? error.message : undefined
    });

  const openCreate = () => {
    setForm(emptyForm);
    setCreateOpen(true);
  };

  const openEdit = (user: ReadUserDto) => {
    setForm({
      name: user.name,
      email: user.email,
      password: "",
      role: user.role
    });
    setEditUser(user);
  };

  const handleCreate = async () => {
    setIsSaving(true);
    try {
      await createUser({
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        role: form.role
      });
      toast.success(t("admin.userCreated"));
      setCreateOpen(false);
    } catch (error) {
      showError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    if (!editUser) return;

    setIsSaving(true);
    try {
      await updateUser(editUser.id, {
        name: form.name.trim(),
        email: form.email.trim(),
        role: form.role,
        ...(form.password && { password: form.password })
      });
      // the sidebar renders name and email from the auth store, not from this list
      if (editUser.id === currentUser?.id) {
        await useAuthStore.getState().validate();
      }
      toast.success(t("admin.userUpdated"));
      setEditUser(null);
    } catch (error) {
      showError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!userToDelete) return;

    setIsDeleting(true);
    try {
      await deleteUser(userToDelete.id);
      toast.success(t("admin.userDeleted"));
      setUserToDelete(null);
    } catch (error) {
      showError(error);
    } finally {
      setIsDeleting(false);
    }
  };

  const isFormValid =
    form.name.trim().length > 0 &&
    form.email.trim().length > 0 &&
    (form.password.length === 0 || form.password.length >= 8);

  const roleSelect = (isSelf: boolean) => (
    <div className="grid gap-2">
      <Label htmlFor="user-role">{t("admin.userRole")}</Label>
      <Select
        value={form.role}
        onValueChange={(role: Role) => setForm({ ...form, role })}
        disabled={isSelf}
      >
        <SelectTrigger id="user-role">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {ROLES.map((role) => (
            <SelectItem key={role} value={role}>
              {roleLabel[role]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {isSelf && (
        <p className="text-xs text-muted-foreground">
          {t("admin.ownRoleLocked")}
        </p>
      )}
    </div>
  );

  return (
    <div className="flex h-full flex-col">
      <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
        <div className="flex w-full items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <SidebarTrigger className="-ml-1" />
            <Separator
              orientation="vertical"
              className="mr-2 data-[orientation=vertical]:h-4"
            />
            <span className="font-medium">{t("admin.users")}</span>
          </div>
          <Button size="sm" onClick={openCreate}>
            <Plus className="size-4" />
            {t("admin.createUser")}
          </Button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-4 p-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="size-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-hidden rounded-md border">
            <ScrollArea className="h-full">
              <Table className="table-fixed">
                <TableHeader className="sticky top-0 z-10 bg-background">
                  <TableRow>
                    <TableHead>{t("admin.userName")}</TableHead>
                    <TableHead>{t("admin.userEmail")}</TableHead>
                    <TableHead className="w-[140px]">
                      {t("admin.userRole")}
                    </TableHead>
                    <TableHead className="w-[180px]">
                      {t("admin.userCreatedAt")}
                    </TableHead>
                    <TableHead className="w-[100px] text-right">
                      {t("admin.actions")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="h-24 text-center text-muted-foreground"
                      >
                        {t("admin.noUsers")}
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="truncate font-medium">
                          {user.name}
                        </TableCell>
                        <TableCell className="truncate text-sm text-muted-foreground">
                          {user.email}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              user.role === "USER" ? "secondary" : "default"
                            }
                          >
                            {roleLabel[user.role]}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(user.createdAt).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => openEdit(user)}
                                >
                                  <Pencil className="size-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                {t("admin.editUser")}
                              </TooltipContent>
                            </Tooltip>
                            {user.id !== currentUser?.id && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setUserToDelete(user)}
                                  >
                                    <Trash2 className="size-4 text-destructive" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {t("admin.deleteUser")}
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </ScrollArea>
          </div>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("admin.createUser")}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="create-name">{t("admin.userName")}</Label>
              <Input
                id="create-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="create-email">{t("admin.userEmail")}</Label>
              <Input
                id="create-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="create-password">{t("admin.userPassword")}</Label>
              <Input
                id="create-password"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            </div>
            {roleSelect(false)}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateOpen(false)}
              disabled={isSaving}
            >
              {t("header.cancel")}
            </Button>
            <Button
              onClick={handleCreate}
              disabled={isSaving || !isFormValid || form.password.length < 8}
            >
              {isSaving && <Loader2 className="mr-2 size-4 animate-spin" />}
              {t("header.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={!!editUser}
        onOpenChange={(open) => !open && setEditUser(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("admin.editUser")}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-user-name">{t("admin.userName")}</Label>
              <Input
                id="edit-user-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-user-email">{t("admin.userEmail")}</Label>
              <Input
                id="edit-user-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-user-password">
                {t("admin.userNewPassword")}
              </Label>
              <Input
                id="edit-user-password"
                type="password"
                placeholder={t("admin.userNewPasswordHint")}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            </div>
            {roleSelect(editUser?.id === currentUser?.id)}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditUser(null)}
              disabled={isSaving}
            >
              {t("header.cancel")}
            </Button>
            <Button onClick={handleSave} disabled={isSaving || !isFormValid}>
              {isSaving && <Loader2 className="mr-2 size-4 animate-spin" />}
              {t("header.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog
        open={!!userToDelete}
        onOpenChange={(open) => !open && setUserToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin.deleteUserTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("admin.deleteUserDescription", { name: userToDelete?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              {t("header.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting && <Loader2 className="mr-2 size-4 animate-spin" />}
              {t("admin.deleteUser")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
